import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Link,
    Chip,
    Pagination,
    Stack
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { getPaginatedBatchConfigurations, BatchConfiguration, downloadBatchConfigurationFile, PaginatedBatchConfigurationResponse } from '../Services/BatchConfigurationService';
import './BatchHistory.css';

const BatchHistory: React.FC = () => {
    const { instance, accounts } = useMsal();
    const [batchConfigurations, setBatchConfigurations] = useState<BatchConfiguration[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const account = accounts[0];

    useEffect(() => {
        loadBatchConfigurations();
    }, [currentPage, pageSize]);

    const loadBatchConfigurations = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getPaginatedBatchConfigurations(instance, currentPage, pageSize, account);
            setBatchConfigurations(response.items);
            setTotalPages(response.totalPages);
            setTotalCount(response.totalCount);
        } catch (err) {
            console.error('Error loading batch configurations:', err);
            setError('Failed to load batch configurations. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusChip = (status: string) => {
        const statusColor = status.toLowerCase() === 'completed' ? 'success' : 
                           status.toLowerCase() === 'in progress' ? 'warning' : 
                           status.toLowerCase() === 'failed' ? 'error' : 'default';
        
        return (
            <Chip 
                label={status} 
                color={statusColor} 
                size="small" 
                variant="outlined"
                className="batch-history-status-chip"
            />
        );
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            // Normalize to UTC if no timezone is present, then convert to browser's local timezone
            const hasTimeZone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
            const normalizedDateString = hasTimeZone ? dateString : `${dateString}Z`;
            const date = new Date(normalizedDateString);
            
            // Format using the system's locale and regional settings with explicit local time indication
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false, // Use 24-hour format, set to true for 12-hour format
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone               
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const handleDownload = async (config: BatchConfiguration) => {
        if (!config.id || !config.blobFileName) {
            console.error('Missing file ID or filename for download');
            return;
        }

        try {
            await downloadBatchConfigurationFile(instance, config.id, config.blobFileName, account);
        } catch (error) {
            console.error('Error downloading file:', error);
            setError('Failed to download file. Please try again.');
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className="batch-history-container">
                <div className="batch-history-loading">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="batch-history-container">
                <Alert severity="error" className="batch-history-error">
                    {error}
                </Alert>
                <Button 
                    onClick={loadBatchConfigurations} 
                    className="batch-history-retry-button"
                    variant="outlined"
                >
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <div className="batch-history-container">
            <Typography variant="h5" component="h1" className="batch-history-header">
                Batch History
                
            </Typography>
            
            {batchConfigurations.length === 0 ? (
                <div className="batch-history-no-data">
                    No batch configurations found.
                </div>
            ) : (
                <>
                    <TableContainer component={Paper} className="batch-history-table-container">
                        <Table>
                            <TableHead className="batch-history-table-header">
                                <TableRow>
                                    <TableCell className="batch-history-table-header-cell">
                                        Company Name
                                    </TableCell>
                                    <TableCell className="batch-history-table-header-cell">
                                        Status
                                    </TableCell>
                                    <TableCell className="batch-history-table-header-cell batch-history-download-column">
                                        Download Link
                                    </TableCell>
                                    <TableCell className="batch-history-table-header-cell">
                                        Last Run Time
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {batchConfigurations.map((config, index) => (
                                    <TableRow 
                                        key={config.id || index}
                                        className={index % 2 === 0 ? 'batch-history-table-row-odd' : 'batch-history-table-row-even'}
                                    >
                                        <TableCell className="batch-history-table-cell">
                                            {config.companyName}
                                        </TableCell>
                                        <TableCell className="batch-history-table-cell">
                                            {getStatusChip(config.status)}
                                        </TableCell>
                                        <TableCell className="batch-history-table-cell batch-history-download-column">
                                            {config.blobFileName && config.status.toLowerCase() === 'completed' ? (
                                                <Link
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => handleDownload(config)}
                                                    className="batch-history-download-link"
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            textDecoration: 'none',
                                                        }
                                                    }}
                                                >
                                                    {config.blobFileName}
                                                </Link>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {config.status.toLowerCase() === 'completed' ? 'No file available' : 'Processing...'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell className="batch-history-table-cell">
                                            {formatDate(config.updatedDate || config.createdDate)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Pagination and Summary */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                shape="rounded"
                                showFirstButton
                                showLastButton
                            />
                        </Stack>
                    </Box>
                </>
            )}
        </div>
    );
};

export default BatchHistory;
